import { ApolloClient, createHttpLink, InMemoryCache } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { getToken, logout } from '../services/auth';
import { onError } from '@apollo/client/link/error';
import { useMemo } from 'react';

const logoutLink = onError(
  ({ graphQLErrors, networkError, forward, operation, ...error }) => {
    if (
      (networkError as any)?.statusCode === 401 ||
      graphQLErrors?.[0]?.extensions?.code === 'UNAUTHENTICATED'
    ) {
      logout();
    } else {
      forward(operation);
    }
  }
);

const authLink = setContext((_, { headers }) => {
  // use the getToken to check for token in local storage
  const token = getToken();
  // return the headers to the context so httpLink can read them
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : '',
    },
  };
});

export type UserMode = 'individual' | 'water-supplier' | 'admin' | 'company';

const useApolloClient = (mode: UserMode | string) => {
  return useMemo(() => {
    const httpLink = createHttpLink({
      uri: (operation) =>
        `${import.meta.env.ECOS_API_BASE}/${
          mode && mode !== 'individual' ? mode + '/' : ''
        }graphql?${operation.operationName ?? ''}`,
    });

    return new ApolloClient({
      link: authLink.concat(logoutLink).concat(httpLink),
      cache: new InMemoryCache({
        typePolicies: {
          BackflowTest: {
            keyFields: ['jobId'],
          },
          User: {
            keyFields: ['profileId'],
          },
        },
        canonizeResults: true,
      }),
    });
  }, [mode]);
};

export default useApolloClient;
